'use client'

import SurveyActionBar from '@/components/embedding/survey/SurveyActionBar'
import SurveySheet from '@/components/embedding/survey/SurveySheet'
import type { SurveySheetState } from '@/components/embedding/survey/useSurveySheet'
import { FC } from 'react'

/**
 * SLIDER/TILE プレイヤーのアンケート描画（#244）。
 *
 * #238（POPUP）の SurveySheet（回答UI）と useSurveySheet（状態機構）を再利用し、
 * SLIDER/TILE では動画下シートではなくパネル/サイドに描画する。
 * - PC: VideoInfoPanel（右パネル）内に節として表示。
 * - SP: SPVideoModal の下部にシートとしてオーバーレイ表示。
 */

/** 展開中の設問シートを描画する共通ホスト。設問が変わるたび key で再マウントし入力を初期化する。 */
export const SurveySheetHost: FC<{ survey: SurveySheetState }> = ({ survey }) => {
  if (!survey.expanded || !survey.activeQuestion) return null
  return (
    <SurveySheet
      key={survey.activeQuestion.id}
      question={survey.activeQuestion}
      isForced={survey.isForced}
      currentIndex={survey.currentIndex}
      total={survey.progress.total}
      initialAnswer={survey.activeAnswer}
      canGoBack={survey.canGoBack}
      onSubmit={survey.onSubmitAnswer}
      onClose={survey.onCloseSheet}
      onBack={survey.onBack}
    />
  )
}

/**
 * PC: VideoInfoPanel 内に表示する、展開中の設問シート。
 * 必須設問は cue 到達で自動展開される（動画は useSurveySheet が一時停止）。
 * 任意設問のオプトインバーは、パネル下端の追従スタック（VideoInfoPanel）が描く。
 */
export const PCSurveySection: FC<{ survey: SurveySheetState }> = ({ survey }) => {
  if (!survey.expanded || !survey.activeQuestion) return null
  return (
    <div className="info-survey-section">
      <div className="info-survey-sheet [&_.moovy-survey-sheet]:!rounded-none">
        <SurveySheetHost survey={survey} />
      </div>
    </div>
  )
}

/**
 * SP: 任意設問のオプトインバー（#244）。
 *
 * 動画に重ねると動画タイトルや「詳しく見る」に被るため、動画の下・CTAボタン行の上に
 * 独立した行として置く（動画個別ページの SP と同じ階層）:
 *   動画（タイトル｜詳しく見る）→ アンケートバー → 別の動画へ｜CTA
 * 展開シート本体は SPSurveySheetPanel が別枠（動画を縮小した下）に描画する。
 */
export const SPSurveyLayer: FC<{
  survey: SurveySheetState
  /** 分岐の選択肢ボタン（左右 16px）の上に並ぶとき、左右の端をそろえる */
  alignWithBranchButtons?: boolean
}> = ({ survey, alignWithBranchButtons = false }) => {
  if (!survey.showTab) return null
  return (
    <div className={`sp-survey-optin${alignWithBranchButtons ? ' sp-survey-optin--branch' : ''}`}>
      <style>{`
        .sp-survey-optin {
          flex-shrink: 0;
          /*
           * 左右は .sp-bottom-section と揃え、下のボタン行と端を合わせる。
           * 上は .sp-video-info-overlay の下padding（16px）が乗るので 0、
           * 下は .sp-bottom-section の上padding（2px）と合わせて 10px にし、
           * タイトル側とボタン側の余白を釣り合わせる。
           */
          padding: 0 12px 8px;
        }
        .sp-survey-optin--branch {
          padding: 0 16px;
        }
        /* SP は動画の下の限られた高さに置くため、PC・動画個別ページ（48px）より低くする */
        .sp-survey-optin .survey-action-bar {
          height: 40px;
        }
      `}</style>
      <SurveyActionBar
        total={survey.progress.total}
        completed={false}
        available
        onOpen={survey.onOpenTab}
      />
    </div>
  )
}

/**
 * SP: アンケート展開シートの表示枠（#244）。
 * 「詳しく見る」の詳細パネルと同じく、動画を上部に縮小（detail-mode）し、
 * 残りの領域をシートが占める（下から迫り出す）。動画にかぶせず縮小表示するため、
 * 従来の絶対オーバーレイではなく sp-modal 直下の flex 子要素として描画する。
 */
export const SPSurveySheetPanel: FC<{ survey: SurveySheetState }> = ({ survey }) => {
  if (!survey.expanded || !survey.activeQuestion) return null
  return (
    <div className="sp-survey-panel">
      <style>{`
        .sp-survey-panel {
          flex: 1;
          min-height: 0;
          overflow: hidden;
          z-index: 30;
        }
      `}</style>
      <SurveySheetHost survey={survey} />
    </div>
  )
}
